import {
  opportunityStageEnum,
  propertyTypeEnum,
  visitAttendees,
  visits,
  visitTasks,
  visitTaskSteps,
} from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'
import { withAttachments } from './attachment'
import { phoneNumberSchema } from './common'
import { acquisitionDataSchema } from './user'

// Auto-generate base schemas
export const selectVisitSchema = createSelectSchema(visits, {
  // Make internal fields optional so they can be filtered out (set to undefined)
  internalNotes: z.string().nullable().optional(),
  internalSafetyNotices: z.string().nullable().optional(),
  internalSafetyNoticesReviewedAt: z.string().nullable().optional(),
  internalSafetyNoticesReviewedBy: z.number().nullable().optional(),
  internalSafetyNoticesReviewComment: z.string().nullable().optional(),
  internalSalesNotes: z.string().nullable().optional(),
})
export const insertVisitSchema = createInsertSchema(visits).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})

export const selectVisitTaskSchema = createSelectSchema(visitTasks)
export const insertVisitTaskSchema = createInsertSchema(visitTasks).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})

export const selectVisitAttendeeSchema = createSelectSchema(visitAttendees)
export const insertVisitAttendeeSchema = createInsertSchema(
  visitAttendees,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})

export const selectVisitTaskStepSchema = createSelectSchema(visitTaskSteps)
export const insertVisitTaskStepSchema = createInsertSchema(
  visitTaskSteps,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})

// Visit task with steps
export const selectVisitTaskWithStepsSchema = selectVisitTaskSchema.extend({
  steps: z.array(selectVisitTaskStepSchema).optional(),
})

// Visit with relations
export const selectVisitWithRelationsSchema = withAttachments(
  selectVisitSchema.extend({
    subscription: z
      .object({
        id: z.number(),
        propertyId: z.number(),
        displayName: z.string().nullable().optional(),
        startDate: z.string(),
        endDate: z.string().nullable(),
        status: z.enum(['proposal', 'active', 'paused', 'cancelled']),
        additionalTerms: z.string().nullable(),
        subscriptionPackageVersions: z
          .array(
            z.object({
              servicePackageVersionId: z.number(),
              sortOrder: z.number().nullable(),
            }),
          )
          .optional(),
      })
      .nullable()
      .optional(),
    property: z
      .object({
        id: z.number(),
        customerId: z.number(),
        propertyType: z.enum(propertyTypeEnum.enumValues),
        customer: z.object({
          id: z.number(),
          name: z.string(),
          type: z.enum(['household', 'company']),
          members: z
            .array(
              z.object({
                id: z.number(),
                role: z.enum(['owner', 'member']),
                user: z.object({
                  id: z.number(),
                  firstName: z.string(),
                  lastName: z.string(),
                  email: z.string().nullable(),
                  phoneNumber: z.string().nullable(),
                }),
              }),
            )
            .optional(),
        }),
        address: z
          .object({
            id: z.number(),
            street: z.string(),
            city: z.string(),
            postalCode: z.string().nullable(),
            country: z.string().nullable(),
            latitude: z.string().nullable(),
            longitude: z.string().nullable(),
          })
          .optional(),
      })
      .nullable()
      .optional(),
    opportunity: z
      .object({
        id: z.number(),
        stage: z.enum(opportunityStageEnum.enumValues),
        customerId: z.number().nullable(),
        user: z.object({
          id: z.number(),
          firstName: z.string(),
          lastName: z.string(),
          email: z.string().nullable(),
          phoneNumber: z.string().nullable(),
        }),
      })
      .nullable()
      .optional(),
    visitTasks: z
      .array(
        selectVisitTaskSchema.extend({
          steps: z.array(selectVisitTaskStepSchema).optional(),
          maintenanceTask: z
            .object({
              id: z.number(),
              name: z.string(),
              description: z.string().nullable(),
              category: z.string().nullable(),
              estimatedDurationMinutes: z.number().nullable(),
            })
            .optional(),
        }),
      )
      .optional(),
    visitAttendees: z
      .array(
        selectVisitAttendeeSchema.extend({
          user: z
            .object({
              id: z.number(),
              firstName: z.string(),
              lastName: z.string(),
              email: z.string(),
            })
            .optional(),
        }),
      )
      .optional(),
    feedbacks: z
      .array(
        z.object({
          id: z.number(),
          ratingValue: z.number(),
        }),
      )
      .optional(),
    internalSafetyNoticesReviewedByUser: z
      .object({
        id: z.number(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
      })
      .nullable()
      .optional(),
  }),
)

// Input schema for creating visit with attendees
export const createVisitWithAttendeesSchema = insertVisitSchema.extend({
  attendees: z
    .array(
      z.object({
        userId: z.number(),
        role: z
          .enum(['lead', 'assistant', 'trainee', 'instructor', 'participant'])
          .optional(),
        status: z.enum(['confirmed', 'tentative', 'declined']).optional(),
        notes: z.string().optional(),
      }),
    )
    .optional(),
})

// Update schemas
export const updateVisitSchema = insertVisitSchema.partial()
export const updateVisitTaskSchema = insertVisitTaskSchema.partial()
export const updateVisitAttendeeSchema = insertVisitAttendeeSchema.partial()
export const updateVisitTaskStepSchema = insertVisitTaskStepSchema.partial()

// Safety notices review schema
export const markSafetyNoticesReviewedSchema = z.object({
  id: z.number(),
  reviewComment: z.string().optional(),
})

// Visit task metadata schemas (for forms and certificates)
export const nuohoustodistusDataSchema = z.object({
  // Fireplace types that were swept
  fireplaceTypes: z.array(z.string()),

  // Fireplaces and flues assessment
  fireplaceStatus: z.enum(['ok', 'deficiency']),
  fireplaceComments: z.string().optional(),

  // Roof safety assessment
  roofSafety: z.enum(['ok', 'deficiency', 'not_ok_not_performed']),
  roofSafetyComments: z.string().optional(),

  // Action required when deficiencies are found
  actionRequired: z
    .enum(['customer_guidance', 'notify_fire_department'])
    .optional(),

  // Next sweeping deadline
  nextSweepingDeadline: z.enum(['1_year', '3_years']),

  // General inspector notes
  inspectorNotes: z.string().optional(),
})

export const visitTaskMetadataSchema = z.discriminatedUnion('formType', [
  z.object({
    formType: z.literal('nuohoustodistus'),
    data: nuohoustodistusDataSchema,
  }),
  // Easy to add more form types in the future:
  // z.object({
  //   formType: z.literal('safety_inspection'),
  //   data: safetyInspectionDataSchema,
  // }),
])

// Public booking schemas
export const bookSurveyVisitSchema = z.object({
  city: z.string().min(1, 'Kaupunki on pakollinen'),
  startTime: z.string().datetime(), // ISO datetime for the booking
  userId: z.number(),
  firstName: z.string().min(1, 'Etunimi on pakollinen'),
  lastName: z.string().min(1, 'Sukunimi on pakollinen'),
  email: z.string().email('Virheellinen sähköpostiosoite'),
  phoneNumber: phoneNumberSchema,
  street: z.string().min(1, 'Osoite on pakollinen'),
  postalCode: z.string().min(1, 'Postinumero on pakollinen'),
  propertyType: z.enum(propertyTypeEnum.enumValues, {
    errorMap: () => ({ message: 'Kiinteistön tyyppi on pakollinen' }),
  }),
  notes: z.string().optional(),
  utmParams: z.record(z.string()).optional(),
})

export const bookSurveyVisitAuthenticatedSchema = z.object({
  city: z.string().min(1, 'Kaupunki on pakollinen'),
  startTime: z.string().datetime(),
  userId: z.number(), // Employee ID
  propertyId: z.number().optional(),
  // User can update their contact details
  firstName: z.string().min(1, 'Etunimi on pakollinen'),
  lastName: z.string().min(1, 'Sukunimi on pakollinen'),
  phoneNumber: z.string().optional(),
  // Address fields when propertyId is not provided
  street: z.string().optional(),
  postalCode: z.string().optional(),
  propertyType: z.enum(propertyTypeEnum.enumValues).optional(),
  notes: z.string().optional(),
  acquisitionData: acquisitionDataSchema.optional(),
})

export const getAvailableTimeSlotsSchema = z.object({})

export const getTimeSlotsForAddressSchema = z.object({
  street: z.string().min(1, 'Osoite on pakollinen'),
  postalCode: z.string().min(1, 'Postinumero on pakollinen'),
  city: z.string().min(1, 'Kaupunki on pakollinen'),
  maxDate: z.string().datetime().optional(),
})

export const bookServiceVisitSchema = z.object({
  startTime: z.string().datetime(),
  employeeId: z.number(),
  firstName: z.string().min(1, 'Etunimi on pakollinen'),
  lastName: z.string().min(1, 'Sukunimi on pakollinen'),
  email: z.string().email('Virheellinen sähköpostiosoite'),
  phoneNumber: phoneNumberSchema,
  street: z.string().min(1, 'Osoite on pakollinen'),
  postalCode: z.string().min(1, 'Postinumero on pakollinen'),
  city: z.string().min(1, 'Kaupunki on pakollinen'),
  propertyType: z.enum(propertyTypeEnum.enumValues, {
    errorMap: () => ({ message: 'Kiinteistön tyyppi on pakollinen' }),
  }),
  notes: z.string().optional(),
  utmParams: z.record(z.string()).optional(),
  maintenanceTaskIds: z
    .array(z.number())
    .min(1, 'Valitse vähintään yksi palvelu'),
  serviceDetails: z.string().optional(),
})

export const availableTimeSlotSchema = z.object({
  startTime: z.string().datetime(),
  endTime: z.string().datetime(),
  userId: z.number(),
})

// Type exports
export type Visit = z.infer<typeof selectVisitSchema>
export type VisitWithRelations = z.infer<typeof selectVisitWithRelationsSchema>
export type VisitTask = z.infer<typeof selectVisitTaskSchema>
export type VisitTaskStep = z.infer<typeof selectVisitTaskStepSchema>
export type VisitTaskWithSteps = z.infer<typeof selectVisitTaskWithStepsSchema>
export type VisitAttendee = z.infer<typeof selectVisitAttendeeSchema>
export type CreateVisit = z.infer<typeof insertVisitSchema>
export type UpdateVisit = z.infer<typeof updateVisitSchema>
export type CreateVisitTask = z.infer<typeof insertVisitTaskSchema>
export type UpdateVisitTask = z.infer<typeof updateVisitTaskSchema>
export type CreateVisitTaskStep = z.infer<typeof insertVisitTaskStepSchema>
export type UpdateVisitTaskStep = z.infer<typeof updateVisitTaskStepSchema>
export type CreateVisitWithAttendees = z.infer<
  typeof createVisitWithAttendeesSchema
>
export type BookSurveyVisit = z.infer<typeof bookSurveyVisitSchema>
export type BookSurveyVisitAuthenticated = z.infer<
  typeof bookSurveyVisitAuthenticatedSchema
>
export type BookServiceVisit = z.infer<typeof bookServiceVisitSchema>
export type GetTimeSlotsForAddress = z.infer<
  typeof getTimeSlotsForAddressSchema
>
export type AvailableTimeSlot = z.infer<typeof availableTimeSlotSchema>
export type NuohoustodistusData = z.infer<typeof nuohoustodistusDataSchema>
export type VisitTaskMetadata = z.infer<typeof visitTaskMetadataSchema>
export type MarkSafetyNoticesReviewed = z.infer<
  typeof markSafetyNoticesReviewedSchema
>
